/** Receiver-only latency tuning and a bounded, cancellable two-minute comparison. */
export type VideoLatencyMode = "normal" | "minimum";
export type VideoLatencySupport = "pending" | "ready" | "unsupported" | "error";

export interface VideoComparisonSummary {
  samples: number;
  measuredSeconds: number;
  jitterMeanMs: number | null;
  jitterP95Ms: number | null;
  decodeMeanMs: number | null;
  fpsMean: number | null;
  droppedFrames: number | null;
  noFramesSeconds: number | null;
}

export interface VideoComparison {
  status: "running" | "complete" | "cancelled" | "failed";
  phase: VideoLatencyMode;
  remainingSeconds: number;
  reason?: string;
  startedAtIso: string;
  normal: VideoComparisonSummary;
  minimum: VideoComparisonSummary;
}

export interface VideoLatencySnapshot {
  latencyMode: VideoLatencyMode;
  latencySupport: VideoLatencySupport;
  requestedBufferMs: number | null;
  comparison: VideoComparison | null;
}

export interface VideoComparisonSample {
  jitterBufferMs: number | null;
  decodeMs: number | null;
  decodedFps: number | null;
  droppedDelta: number | null;
  intervalMs: number | null;
}

interface ReceiverLike {
  jitterBufferTarget?: number | null;
}
interface SlotControl {
  mode: VideoLatencyMode;
  support: VideoLatencySupport;
  target: number | null;
  comparison: VideoComparison | null;
  samples: Record<VideoLatencyMode, VideoComparisonSample[]>;
  restoreMode: VideoLatencyMode;
  startedAt: number;
}

const PHASE_MS = 60_000;
const SETTLE_MS = 5_000;

function summarize(samples: VideoComparisonSample[]): VideoComparisonSummary {
  function weighted(key: "jitterBufferMs" | "decodeMs" | "decodedFps") {
    const available = samples.filter((s) => s[key] !== null);
    const duration = available.reduce((sum, s) => sum + (s.intervalMs ?? 0), 0);
    return duration
      ? available.reduce((sum, s) => sum + s[key]! * s.intervalMs!, 0) / duration
      : null;
  }
  const jitter = samples
    .flatMap((s) => (s.jitterBufferMs === null ? [] : [s.jitterBufferMs]))
    .sort((a, b) => a - b);
  const drops = samples.flatMap((s) => (s.droppedDelta === null ? [] : [s.droppedDelta]));
  const fps = samples.filter((s) => s.decodedFps !== null);
  return {
    samples: samples.length,
    measuredSeconds: samples.reduce((sum, s) => sum + (s.intervalMs ?? 0), 0) / 1000,
    jitterMeanMs: weighted("jitterBufferMs"),
    // Percentile of the one-second interval means, not of individual video frames.
    jitterP95Ms: jitter.length ? (jitter[Math.ceil(jitter.length * 0.95) - 1] ?? null) : null,
    decodeMeanMs: weighted("decodeMs"),
    fpsMean: weighted("decodedFps"),
    droppedFrames: drops.length ? drops.reduce((sum, n) => sum + n, 0) : null,
    noFramesSeconds: fps.length
      ? fps.filter((s) => s.decodedFps === 0).reduce((sum, s) => sum + s.intervalMs!, 0) / 1000
      : null,
  };
}

export function createVideoLatencyControl({
  getReceiver,
  onUpdate,
  now = () => performance.now(),
}: {
  getReceiver: (slot: number) => ReceiverLike | undefined;
  onUpdate: (slot: number) => void;
  now?: () => number;
}) {
  const slots = new Map<number, SlotControl>();
  let timer: ReturnType<typeof setInterval> | null = null;

  function state(slot: number): SlotControl {
    let s = slots.get(slot);
    if (!s) {
      s = {
        mode: "normal",
        support: "pending",
        target: null,
        comparison: null,
        samples: { normal: [], minimum: [] },
        restoreMode: "normal",
        startedAt: 0,
      };
      slots.set(slot, s);
    }
    return s;
  }

  function apply(slot: number) {
    const s = state(slot);
    const receiver = getReceiver(slot);
    s.target = null;
    if (!receiver) {
      s.support = "pending";
      return;
    }
    if (!("jitterBufferTarget" in receiver)) {
      s.support = "unsupported";
      return;
    }
    try {
      const target = s.mode === "minimum" ? 0 : null;
      receiver.jitterBufferTarget = target;
      s.support = receiver.jitterBufferTarget === target ? "ready" : "error";
      if (s.support === "ready") s.target = target;
    } catch {
      s.support = "error";
    }
  }

  function stopTimerIfIdle() {
    if (![...slots.values()].some((s) => s.comparison?.status === "running") && timer) {
      clearInterval(timer);
      timer = null;
    }
  }

  function finish(slot: number, status: VideoComparison["status"], reason?: string) {
    const s = state(slot);
    if (!s.comparison || s.comparison.status !== "running") return;
    s.comparison = {
      ...s.comparison,
      status,
      reason,
      remainingSeconds: 0,
      normal: summarize(s.samples.normal),
      minimum: summarize(s.samples.minimum),
    };
    s.mode = s.restoreMode;
    apply(slot);
    stopTimerIfIdle();
    onUpdate(slot);
  }

  function tick() {
    for (const [slot, s] of slots) {
      if (s.comparison?.status !== "running") continue;
      const elapsed = now() - s.startedAt;
      if (!getReceiver(slot)) {
        finish(slot, "failed", "Поток отключился");
        continue;
      }
      if (elapsed >= PHASE_MS && s.comparison.phase === "minimum") {
        const enough = [s.samples.normal, s.samples.minimum].every((samples) => {
          const summary = summarize(samples);
          return (
            summary.measuredSeconds >= 30 &&
            summary.jitterMeanMs !== null &&
            summary.fpsMean !== null
          );
        });
        finish(
          slot,
          enough ? "complete" : "failed",
          enough
            ? undefined
            : "Недостаточно свежих измерений буфера и FPS: нужно не менее 30 с на режим"
        );
        continue;
      }
      if (elapsed >= PHASE_MS && s.comparison.phase === "normal") {
        s.mode = "minimum";
        apply(slot);
        if (s.support !== "ready") {
          finish(slot, "failed", "Браузер не применил режим");
          continue;
        }
        s.comparison = { ...s.comparison, phase: "minimum" };
        s.startedAt = now();
      }
      const remaining =
        (s.comparison.phase === "normal" ? PHASE_MS * 2 : PHASE_MS) - (now() - s.startedAt);
      s.comparison = {
        ...s.comparison,
        remainingSeconds: Math.ceil(remaining / 1000),
        normal: summarize(s.samples.normal),
        minimum: summarize(s.samples.minimum),
      };
      onUpdate(slot);
    }
  }

  return {
    snapshot(slot: number): VideoLatencySnapshot {
      const s = state(slot);
      return {
        latencyMode: s.mode,
        latencySupport: s.support,
        requestedBufferMs: s.target,
        comparison: s.comparison,
      };
    },
    setMode(slot: number, mode: VideoLatencyMode) {
      const s = state(slot);
      if (s.comparison?.status === "running") finish(slot, "cancelled");
      s.mode = mode;
      apply(slot);
      onUpdate(slot);
    },
    receiverChanged(slot: number) {
      finish(slot, "failed", "Поток переподключился; повторите сравнение");
      apply(slot);
      onUpdate(slot);
    },
    startComparison(slot: number): boolean {
      const s = state(slot);
      if (s.comparison?.status === "running") return false;
      apply(slot);
      if (s.support !== "ready") {
        onUpdate(slot);
        return false;
      }
      s.restoreMode = s.mode;
      s.startedAt = now();
      s.samples = { normal: [], minimum: [] };
      s.mode = "normal";
      apply(slot);
      if (s.support !== "ready") {
        s.mode = s.restoreMode;
        apply(slot);
        onUpdate(slot);
        return false;
      }
      s.comparison = {
        status: "running",
        phase: "normal",
        remainingSeconds: 120,
        startedAtIso: new Date().toISOString(),
        normal: summarize([]),
        minimum: summarize([]),
      };
      if (!timer) timer = setInterval(tick, 1000);
      onUpdate(slot);
      return true;
    },
    cancelComparison(slot: number) {
      finish(slot, "cancelled");
    },
    record(slot: number, sample: VideoComparisonSample) {
      const s = state(slot);
      if (
        s.comparison?.status !== "running" ||
        !sample.intervalMs ||
        sample.intervalMs <= 0 ||
        sample.intervalMs > 2500
      )
        return;
      const phaseElapsed = now() - s.startedAt;
      // Exclude whole intervals spanning warm-up, a phase boundary or delayed polls.
      if (phaseElapsed < SETTLE_MS + sample.intervalMs || phaseElapsed >= PHASE_MS) return;
      s.samples[s.comparison.phase].push({ ...sample });
    },
    destroy() {
      for (const [slot] of slots) finish(slot, "cancelled");
      if (timer) clearInterval(timer);
      timer = null;
    },
  };
}
